// Layer 4 (UI) — the Cost per tonne tab, per the locked comp (variant A,
// picked 25 Sep): one Cost build-up card. The Cost / dst figure and its
// Budget rate lead; the five categories follow as collapsible rows, each
// naming its line count; an open category lists its lines. The category
// variances are the band's ranking, so no variance cards repeat them here,
// and the daily-margin bars live in the band. Presentational only.

import { Fragment } from 'react';
import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { vizToneColor } from '@/versions/v1/modules/_viz/viz-tone';
import { CARD_TABLE_DENSE, DowntimeCard, SYN_CHIP } from '@/versions/v1/modules/downtime/DowntimeCard';
import { PER_DST_HEADING, costBuildUpTable, type CostBuildUpCategoryCells } from './CostPerTonne.transform';
import type { CostCategoryName, CostPeriod } from './Scene.transform';

function varianceStyle(tone: CostBuildUpCategoryCells['varianceTone'] | 'bad') {
  if (tone === 'warn' || tone === 'ok' || tone === 'bad') return { color: vizToneColor(tone) };
  return undefined;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      aria-hidden
      className={cn('size-3 shrink-0 text-muted transition-transform duration-150', open && 'rotate-90')}
    >
      <path d="M4.5 2.5 8 6l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function CostPerTonne({
  period,
  openCategories,
  onToggleCategory,
}: {
  period: CostPeriod;
  openCategories: readonly CostCategoryName[];
  onToggleCategory: (category: CostCategoryName) => void;
}) {
  const view = costBuildUpTable(period);

  return (
    <Commentable name="CostCentreTable">
      <div>
        <DowntimeCard
          title="Cost build-up"
          meta={view.tonnesMilled}
          actions={SYN_CHIP}
        >
          <div className="mb-3 border-b border-[var(--hairline)] pb-3" data-cost-per-dst>
            <p className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">Cost / dst</p>
            <p className="mt-1 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
              {view.figure}
              <span className="text-sm font-semibold tracking-normal text-ink-secondary">{view.unit}</span>
            </p>
            <p className="mt-1 text-xs tabular-nums text-muted">
              <span className="font-semibold text-ink-secondary">vs Budget</span> {view.budgetComparison}
            </p>
          </div>
          <div className={CARD_TABLE_DENSE}>
            <Table wrap={false} className="tabular-nums" aria-label="Cost build-up">
              <THead>
                <TR>
                  <TH>Line</TH>
                  <TH className="text-right">Qty</TH>
                  <TH className="text-right">Rate</TH>
                  <TH className="text-right">Budget</TH>
                  <TH className="text-right">Actual</TH>
                  <TH className="text-right">Variance</TH>
                  <TH className="text-right">{PER_DST_HEADING}</TH>
                </TR>
              </THead>
              <TBody>
                {view.categories.map((row) => {
                  const open = openCategories.includes(row.category);
                  return (
                    <Fragment key={row.category}>
                      <TR className="bg-[var(--surface-1)]" data-build-up-category={row.category}>
                        <TD colSpan={3} className="whitespace-nowrap">
                          <button
                            type="button"
                            aria-expanded={open}
                            onClick={() => onToggleCategory(row.category)}
                            className="flex items-center gap-2 text-left"
                          >
                            <Chevron open={open} />
                            <span data-build-up-label className="text-xs font-semibold uppercase tracking-[0.07em] text-ink-secondary">
                              {row.category}
                            </span>
                            <span className="text-xs text-muted">{row.count}</span>
                          </button>
                        </TD>
                        <TD className="whitespace-nowrap text-right text-muted">{row.budget}</TD>
                        <TD className="whitespace-nowrap text-right font-semibold text-ink-primary">{row.actual}</TD>
                        <TD
                          className={cn(
                            'whitespace-nowrap text-right',
                            row.varianceTone === 'zero' ? 'text-muted' : 'font-semibold',
                            row.varianceTone === 'neutral' && 'text-ink-primary',
                          )}
                          style={varianceStyle(row.varianceTone)}
                        >
                          {row.variance}
                        </TD>
                        <TD className="whitespace-nowrap text-right text-muted">{row.perDst}</TD>
                      </TR>
                      {open
                        ? row.lines.map((line) => (
                            <TR key={`${row.category}-${line.label}`} data-build-up-line={row.category}>
                              <TD className="whitespace-nowrap !pl-12 text-ink-primary">{line.label}</TD>
                              <TD className="whitespace-nowrap text-right text-muted">{line.qty}</TD>
                              <TD className="whitespace-nowrap text-right text-muted">{line.rate}</TD>
                              <TD />
                              <TD className="whitespace-nowrap text-right text-ink-primary">{line.actual}</TD>
                              <TD />
                              <TD className="whitespace-nowrap text-right text-muted">{line.perDst}</TD>
                            </TR>
                          ))
                        : null}
                    </Fragment>
                  );
                })}
                <TR className="border-t-2 border-[var(--hairline-strong)]">
                  <TD colSpan={3} className="whitespace-nowrap font-semibold text-ink-primary">Total operating cost</TD>
                  <TD className="whitespace-nowrap text-right font-semibold text-ink-primary">{view.total.budget}</TD>
                  <TD className="whitespace-nowrap text-right font-semibold text-ink-primary">{view.total.actual}</TD>
                  <TD className="whitespace-nowrap text-right font-semibold" style={varianceStyle(view.total.varianceTone)}>
                    {view.total.variance}
                  </TD>
                  <TD className="whitespace-nowrap text-right font-semibold text-ink-primary">{view.total.perDst}</TD>
                </TR>
              </TBody>
            </Table>
          </div>
        </DowntimeCard>
      </div>
    </Commentable>
  );
}
